import React from "react";
import { Languages } from "lucide-react";

import usePostCaptionTranslation from "../../hooks/usePostCaptionTranslation";
import { POST_TRANSLATION_LANGUAGES } from "../../services/postTranslationApi";
import "../../styles/components/PostTranslation.css";
import PostCaption from "./PostCaption";

const ACTIVE_LANGUAGE_SET = new Set(POST_TRANSLATION_LANGUAGES);

function languageName(t, language) {
  if (!ACTIVE_LANGUAGE_SET.has(language)) return "";
  return t(`post_translation_language_name_${language}`);
}

export default function TranslatablePostCaption({
  postId,
  postType,
  text,
}) {
  const translation = usePostCaptionTranslation({
    postId,
    postType,
    caption: text,
  });
  const { t } = translation;
  const targetName = languageName(t, translation.targetLanguage);
  const sourceName = languageName(t, translation.sourceLanguage);
  const translatedLabel = sourceName
    ? t("post_translation_translated_from", { language: sourceName })
    : t("post_translation_translated");

  return (
    <div className="rp-post-translation">
      <PostCaption text={translation.visibleText} t={t} />

      <div
        className="rp-post-translation__status"
        aria-live={translation.status === "idle" ? "off" : "polite"}
        aria-atomic="true"
      >
        {translation.translationAvailable &&
        translation.status === "idle" &&
        targetName ? (
          <button
            type="button"
            className="rp-post-translation__action"
            onClick={translation.requestTranslation}
          >
            <Languages
              className="rp-post-translation__icon"
              size={15}
              aria-hidden="true"
              focusable="false"
            />
            <span>
              {t("post_translation_translate_to", { language: targetName })}
            </span>
          </button>
        ) : null}

        {translation.status === "loading" ? (
          <button
            type="button"
            className="rp-post-translation__action"
            disabled
            aria-busy="true"
          >
            <Languages
              className="rp-post-translation__icon"
              size={15}
              aria-hidden="true"
              focusable="false"
            />
            <span>{t("post_translation_translating")}</span>
          </button>
        ) : null}

        {translation.hasTranslation &&
        (translation.status === "translated" ||
          translation.status === "showingOriginal") ? (
          <div className="rp-post-translation__result">
            <span>{translatedLabel}</span>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              className="rp-post-translation__inline-action"
              onClick={
                translation.status === "translated"
                  ? translation.showOriginal
                  : translation.showTranslation
              }
            >
              {t(
                translation.status === "translated"
                  ? "post_translation_see_original"
                  : "post_translation_see_translation"
              )}
            </button>
          </div>
        ) : null}

        {translation.status === "error" ? (
          <div className="rp-post-translation__result rp-post-translation__result--error">
            <span>{t("post_translation_unavailable")}</span>
            <button
              type="button"
              className="rp-post-translation__inline-action"
              onClick={translation.retryTranslation}
            >
              {t("post_translation_retry")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
